import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { extensionTypeOptionActions } from 'src/app/common-components/entity-select/extension-type/state/extension-type-option.actions';
import { extensionTypeOptionSelectors } from 'src/app/common-components/entity-select/extension-type/state/extension-type-option.reducer';
import { FormComponent } from 'src/app/common-components/forms-base/form-component.interface';
import { AccountDetailFormService } from 'src/app/pages/account-details/services/account-detail-form.service';
import { AddAccountData } from 'src/app/pages/add-account/models/add-account-data';
import { createAccount } from 'src/app/pages/add-account/state/add-account.actions';
import { FormatPhoneNumbersPipe } from 'src/app/pipes/format-phone-numbers/format-phone-numbers.pipe';
import { ExtensionDetailView } from '../../extension-details/models/extension-detail-view.model';
import { ExtensionTypeOption } from '../../extension-register/models/extension-type-option.interface';
import { LocationDetailFormValue } from '../../location-details/models/location-detail-form';
import { LocationDetailView } from '../../location-details/models/location-detail-view.model';
import { PhoneNumberDetailView } from '../../phone-number-details/models/phone-number-detail-view.model';
import { AddAccountExtensionComponent } from '../add-account-extension/containers/add-account-extension.component';
import { AccountExtensionView } from '../add-account-extension/models/account-extension-view.model';
import { AddAccountLocationComponent } from '../add-account-location/containers/add-account-locations.component';
import { AddAccountPhoneNumberComponent } from '../add-account-phone-numbers/containers/add-account-phone-numbers.component';
import { AccountPhoneNumberView } from '../add-account-phone-numbers/models/account-phone-number-view.model';
import { getAddAccountCallState } from '../state/add-account.selectors';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [AccountDetailFormService],
    selector: 'ndt-add-account',
    styleUrls: ['./add-account.component.scss'],
    templateUrl: './add-account.component.html',
    standalone: false
})
export class AddAccountComponent implements OnInit, FormComponent, OnDestroy {
   form: UntypedFormGroup;

   callState$: Observable<CallState>;

   createExtensionTypeOptions$: Observable<ExtensionTypeOption[]>;

   get extensionsControl(): AbstractControl | null {
      return this.form.get('newExtensions') ?? null;
   }

   get phoneNumbersControl(): AbstractControl | null {
      return this.form.get('newPhoneNumbers') ?? null;
   }

   get locationsControl(): AbstractControl | null {
      return this.form.get('newAccountLocations') ?? null;
   }

   private destroyed$ = new Subject<void>();

   locationRequired = true;

   constructor(
      protected store: Store,
      protected formService: AccountDetailFormService,
      @Inject(MAT_DIALOG_DATA) public data: AddAccountData,
      private dialogService: LcsDialogService,
      private confirmationService: ConfirmationDialogService
   ) {
      this.callState$ = this.store.pipe(select(getAddAccountCallState));
      this.store.dispatch(extensionTypeOptionActions.load({}));
      this.createExtensionTypeOptions$ = this.store.pipe(select(extensionTypeOptionSelectors.selectFilteredItems));
   }

   ngOnInit(): void {
      this.form = this.formService.form;
      this.form
         .get('name')
         ?.valueChanges.pipe(takeUntil(this.destroyed$))
         .subscribe((name) => {
            this.form.get('shortName')?.setValue(
               name
                  .replace(/[^a-zA-Z0-9]/g, '')
                  .slice(0, 50)
                  .toLowerCase()
            );
         });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onCloseClick(): void {
      this.dialogService.close();
   }

   onCreate(): void {
      if (this.form.valid) {
         this.store.dispatch(
            createAccount({
               account: this.formService.getValue(null),
            })
         );
      }
   }

   onRemoveExtension(data: { extensionNumber: number; phoneNumbers: AccountPhoneNumberView[] }): void {
      if (data.phoneNumbers.length > 0) {
         const formatPhonePipe = new FormatPhoneNumbersPipe();

         const formattedPhoneNumbers = data.phoneNumbers
            .map((phone) => formatPhonePipe.transform(phone.phoneNumber))
            .reduce((acc, curr, index, array) => {
               if (index === 0) return curr;
               if (index === array.length - 1) return `${acc} and ${curr}`;
               return `${acc}, ${curr}`;
            });

         const message = `Extension ${data.extensionNumber} is being used as the Route To extension for phone number${
            data.phoneNumbers.length > 1 ? 's' : ''
         } ${formattedPhoneNumbers}.\n\nAre you sure you want to remove it?`;

         const dialogRef = this.confirmationService.confirm({
            data: {
               header: 'Remove Extension Confirmation',
               message,
            },
            size: DialogSize.Medium,
         });

         dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
               data.phoneNumbers.forEach((phone) => {
                  this.removeExtensionAndUpdatePhoneNumber(data.extensionNumber, phone.phoneNumber);
               });
            }
         });
      } else {
         this.removeExtension(data.extensionNumber);
      }
   }

   private removeExtensionAndUpdatePhoneNumber(extensionNumber: number, phoneNumber: string): void {
      this.removeExtension(extensionNumber);

      if (this.phoneNumbersControl?.value) {
         const phoneNumbers = this.phoneNumbersControl.value;
         const updatedPhoneNumbers = phoneNumbers.map((phone: AccountPhoneNumberView) => {
            if (phone.phoneNumber === phoneNumber) {
               return { ...phone, routeTo: null };
            }
            return phone;
         });
         this.phoneNumbersControl.patchValue(updatedPhoneNumbers);
      }
   }

   private removeExtension(extensionNumber: number): void {
      if (this.extensionsControl?.value) {
         const extensions = this.extensionsControl.value;
         const updatedExtensions = extensions.filter((ext: any) => ext.number !== extensionNumber);
         this.extensionsControl.patchValue(updatedExtensions);
      }
   }

   onAddExtension(type: ExtensionTypeOption): void {
      const dialogRef = this.openAccountExtensionDialog(type);
      dialogRef.afterClosed().subscribe((extensions) => {
         this.addExtensionsToForm(extensions);
      });
   }

   onEditExtension(extension: AccountExtensionView) {
      const ext: ExtensionDetailView = {
         extensionId: 0,
         ...extension,
      };
      const dialogRef = this.openAccountExtensionDialog(extension.type, ext);
      dialogRef.afterClosed().subscribe((extensions) => {
         this.addExtensionsToForm(extensions);
      });
   }

   onEditLocation(location: LocationDetailView) {
      const dialogRef = this.openAccountLocationDialog(location);
      dialogRef.afterClosed().subscribe((locations) => {
         this.addLocationsToForm(locations);
      });
   }

   onAddPhoneNumber(isNew: boolean): void {
      const dialogRef = this.openAccountPhoneNumberDialog(isNew);
      dialogRef.afterClosed().subscribe((phoneNumbers) => {
         this.addPhoneNumbersToForm(phoneNumbers);
      });
   }

   onAddLocation(): void {
      const dialogRef = this.openAccountLocationDialog();
      dialogRef.afterClosed().subscribe((locations) => {
         this.addLocationsToForm(locations);
      });
   }

   private openAccountExtensionDialog(
      createExtensionTypeOption?: ExtensionTypeOption,
      item?: ExtensionDetailView
   ): MatDialogRef<AddAccountExtensionComponent> {
      const data = {
         extension: {
            ...item,
            type: item?.type ?? createExtensionTypeOption,
         },
         existingExtensions: this.extensionsControl?.value,
      };

      const dialogRef = this.dialogService.openFormComponent(AddAccountExtensionComponent, {
         closeOnNavigation: true,
         data,
         disableClose: true,
         panelClass: ['add-dialog-panel', 'dialog-container'],
      });

      return dialogRef;
   }

   private openAccountLocationDialog(item?: LocationDetailView): MatDialogRef<AddAccountLocationComponent> {
      const data = {
         location: {
            ...item,
         },
         existingLocations: this.locationsControl?.value,
      };

      const dialogRef = this.dialogService.openFormComponent(AddAccountLocationComponent, {
         closeOnNavigation: true,
         data,
         disableClose: true,
         panelClass: ['add-dialog-panel', 'dialog-container'],
      });

      return dialogRef;
   }

   private openAccountPhoneNumberDialog(
      isNew: boolean,
      item?: PhoneNumberDetailView
   ): MatDialogRef<AddAccountPhoneNumberComponent> {
      const data = {
         phoneNumber: {
            ...item,
         },
         isNew,
         existingPhoneNumbers: this.phoneNumbersControl?.value,
         existingExtensions: this.extensionsControl?.value,
      };

      const dialogRef = this.dialogService.openFormComponent(AddAccountPhoneNumberComponent, {
         closeOnNavigation: true,
         data,
         disableClose: true,
         panelClass: ['add-dialog-panel', 'dialog-container'],
      });

      return dialogRef;
   }

   private addExtensionsToForm(extensions: ExtensionDetailView[]) {
      if (this.extensionsControl && extensions?.length > 0) {
         this.extensionsControl.setValue([...extensions]);
      }
   }

   private addPhoneNumbersToForm(phoneNumbers: PhoneNumberDetailView[]) {
      if (this.phoneNumbersControl && phoneNumbers?.length > 0) {
         this.phoneNumbersControl.patchValue([...this.phoneNumbersControl.value]);
      }
   }

   private addLocationsToForm(locations: LocationDetailFormValue[]) {
      if (this.locationsControl && locations.length > 0) {
         this.locationsControl.setValue([...locations]);
      }
   }
}
